/**
 * The one way to hear Tibbs: a button, like Single Didge's "Blair's Readout".
 * Idle → "Hear Tibbs"; while the readout is written and voiced → a rotating
 * waiting line ("Tibbs is clearing his throat…"); while he talks → Stop; when
 * audio is locked (an online request before this device was ever tapped) →
 * Play; afterward → Replay; after a failure → Try again. The caption sits under it.
 */
import type { TibbsStatus } from '../../tibbs/useTibbs';
import { Tibbs } from '../Art';
import { AmphoraIcon } from '../Icons';

export function TibbsButton({
  status,
  kind,
  loadingText = 'Tibbs is clearing his throat…',
  onRequest,
  onStop,
  onResume,
  className = '',
}: {
  status: TibbsStatus;
  kind: 'readout' | 'ceremony';
  loadingText?: string;
  onRequest: () => void;
  onStop: () => void;
  onResume: () => void;
  className?: string;
}) {
  const icon = kind === 'ceremony' ? <AmphoraIcon size={20} /> : <Tibbs pose="wry" size={26} className="tibbs-btn-face" />;
  const label = kind === 'ceremony' ? 'Presentation of the Golden Amphora' : 'Hear Tibbs';
  const again = kind === 'ceremony' ? 'Replay the presentation' : 'Hear Tibbs again';
  const common = `btn tibbs-btn tb-${kind}${className ? ` ${className}` : ''}`;
  switch (status) {
    case 'loading':
      return (
        <button type="button" className={`${common} live`} disabled aria-busy="true">
          <span className="spinner small" aria-hidden="true" /> <span className="tibbs-loading">{loadingText}</span>
        </button>
      );
    case 'playing':
      return (
        <button type="button" className={`${common} live`} onClick={onStop} aria-label="Stop Tibbs">
          <span className="tibbs-stop" aria-hidden="true" /> Stop
        </button>
      );
    case 'blocked':
      return (
        <button type="button" className={`${common} live`} onClick={onResume}>
          {icon} Play
        </button>
      );
    case 'failed':
      return (
        <button type="button" className={common} onClick={onRequest}>
          {icon} Try again
        </button>
      );
    case 'done':
      return (
        <button type="button" className={common} onClick={onRequest}>
          {icon} {again}
        </button>
      );
    default:
      return (
        <button type="button" className={common} onClick={onRequest}>
          {icon} {label}
        </button>
      );
  }
}

/** Tibbs's current line, in one italic line (screen readers get it from the table's live region). */
export function TibbsCaption({ text }: { text: string | null }) {
  return (
    <p className={`tibbs-caption${text ? ' on' : ''}`} aria-hidden="true">
      {text ? <span key={text}>{text}</span> : null}
    </p>
  );
}
